// TES-1881: von DefectBaseDialog konsumiert (Page-Wiring Follow-up). Unit-getestet.
// fallow-ignore-file unused-file
// fallow-ignore-file unused-export
// fallow-ignore-file complexity
// TES-1881 / TES-1860 (v2.3 §5) — Fachabteilung (Pflicht) + Bearbeiter (optional) via AssignmentCell.
// Reine Darstellungs-/Steuer-Komponente: das Öffnen der Picker (Layered Modal + QuickCreate) liefert der
// Host (DefectBaseDialog) über onPickGroup/onPickAssignee, damit diese Komponente ohne Modal-Host testbar
// bleibt. "Ich übernehme" ruft den Self-Register-Pfad (defect_self_assign, Mig 334).
// TES-1990 (v2.3 F-RBAC-OFFB-05): Gruppen-OWNER verteilen die Queue ihrer Gruppe über den
// spalten-engen RPC defect_owner_assign (Mig 369) — die Owner-Buttons erscheinen wie der
// Self-Register auch im View-Modus (canEdit gated nur die AssignmentCells).
import { useTranslation } from "react-i18next";
import { AssignmentCell, type AssignmentValue } from "@plasser/ui";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { UserCheck, UserMinus, UserPlus } from "lucide-react";
import { cn } from "@/lib/utils";

export interface DefectGroupAssignmentProps {
  /** Fachabteilung (Pflicht). */
  group: AssignmentValue | null;
  /** Individueller Bearbeiter (optional). */
  assignee: AssignmentValue | null;
  /** Editierbar (UI-Gate; RLS bleibt Wahrheit). */
  canEdit?: boolean;
  /** Self-Register sichtbar (Gruppen-Mitglied ohne aktuellen Bearbeiter). */
  canSelfAssign?: boolean;
  /** Owner-Zuweisung sichtbar (Gruppen-OWNER ohne regulären Edit-Pfad, TES-1990). */
  canOwnerAssign?: boolean;
  /** Pflicht-Validierung: Gruppe fehlt. */
  showGroupError?: boolean;
  onPickGroup: () => void;
  onClearGroup?: () => void;
  onPickAssignee: () => void;
  onClearAssignee?: () => void;
  onSelfAssign?: () => void;
  /** Owner wählt einen Bearbeiter aus den Gruppen-Mitgliedern (RPC defect_owner_assign). */
  onOwnerAssign?: () => void;
  /** Owner stellt den Mangel zurück in die Gruppen-Queue (RPC, _assignee = NULL). */
  onOwnerUnassign?: () => void;
}

export function DefectGroupAssignment({
  group,
  assignee,
  canEdit = false,
  canSelfAssign = false,
  canOwnerAssign = false,
  showGroupError = false,
  onPickGroup,
  onClearGroup,
  onPickAssignee,
  onClearAssignee,
  onSelfAssign,
  onOwnerAssign,
  onOwnerUnassign,
}: DefectGroupAssignmentProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-3" data-testid="defect-group-assignment">
      <div className="space-y-1">
        <Label className="flex items-center gap-1">
          {t("defects.assignmentSection.groupLabel")}
          <span className="text-destructive">*</span>
        </Label>
        <div
          aria-invalid={showGroupError || undefined}
          data-testid="defect-group-cell"
          className={cn(showGroupError && "rounded-md ring-1 ring-destructive")}
        >
          <AssignmentCell
            value={group}
            onAssign={onPickGroup}
            onClear={group && onClearGroup ? onClearGroup : undefined}
            placeholder={t("defects.assignmentSection.groupPlaceholder")}
            disabled={!canEdit}
          />
        </div>
        {showGroupError && (
          <p className="text-xs text-destructive" data-testid="defect-group-error">
            {t("defects.assignmentSection.groupRequired")}
          </p>
        )}
      </div>

      <div className="space-y-1">
        <Label>{t("defects.assignmentSection.assigneeLabel")}</Label>
        <div className="flex items-center gap-2">
          <AssignmentCell
            value={assignee}
            onAssign={onPickAssignee}
            onClear={assignee && onClearAssignee ? onClearAssignee : undefined}
            placeholder={t("defects.assignmentSection.assigneePlaceholder")}
            disabled={!canEdit}
          />
          {canSelfAssign && !assignee && onSelfAssign && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onSelfAssign}
              data-testid="defect-self-assign"
            >
              <UserCheck className="mr-1 h-3.5 w-3.5" />
              {t("defects.assignmentSection.selfAssign")}
            </Button>
          )}
          {canOwnerAssign && !assignee && onOwnerAssign && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onOwnerAssign}
              data-testid="defect-owner-assign"
            >
              <UserPlus className="mr-1 h-3.5 w-3.5" />
              {t("defects.assignmentSection.ownerAssign")}
            </Button>
          )}
          {canOwnerAssign && assignee && onOwnerUnassign && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onOwnerUnassign}
              data-testid="defect-owner-unassign"
            >
              <UserMinus className="mr-1 h-3.5 w-3.5" />
              {t("defects.assignmentSection.ownerUnassign")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
